import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr } from "@/i18n/copy";
import { useWorkflowGuide } from "@/components/WorkflowGuide";
import { Check, Lightbulb } from "lucide-react";
import { clientBookingProgress } from "@/bookingGuidance";

export function useBookingGuide(userId, relationshipId) {
  return useWorkflowGuide("booking-request", userId ? `user:${userId}` : relationshipId ? `relationship:${relationshipId}` : "");
}

export function BookingGuideButton({ guide }) {
  useUiLanguage();
  return <button ref={guide.buttonRef} type="button" onClick={guide.toggle}
    aria-label={tr("Aiuto per richieste e appuntamenti")} aria-expanded={guide.visible} aria-controls={guide.id}
    className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
    <span aria-hidden="true" className="grid h-6 w-6 place-items-center rounded-full border text-sm font-semibold">?</span>
  </button>;
}

export function BookingGuideHint({ guide, hasProposal }) {
  useUiLanguage();
  return <div id={guide.id} hidden={!guide.visible} data-testid="booking-guide-hint">
    {guide.visible && <div className="flex items-start gap-2 rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm">
      <Lightbulb aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1"><p>{hasProposal
        ? tr("Apri la proposta, controlla i dettagli e scegli come rispondere. Le tappe mostrano a che punto sei.")
        : tr("Apri una richiesta per seguirne le tappe e vedere il prossimo passo. La data è confermata solo quando lo indica l’appuntamento.")}</p>
        <button type="button" onClick={guide.acknowledge} className="mt-1 min-h-11 font-semibold text-primary underline-offset-4 hover:underline">{tr("Ho capito")}</button>
      </div>
    </div>}
  </div>;
}

export function BookingProgress({ booking, proposal, appointment }) {
  useUiLanguage();
  const progress = clientBookingProgress(booking, proposal, appointment);
  if (!progress) return null;
  return <div className="mb-3" data-testid="booking-progress">
    <ol aria-label={tr("Avanzamento della richiesta")} className="flex items-start">
      {progress.steps.map((step, index) => {
        const complete = progress.complete || index < progress.current;
        const current = !progress.complete && index === progress.current;
        return <li key={step} aria-current={current ? "step" : undefined} className="relative min-w-0 flex-1 text-center">
          {index > 0 && <span aria-hidden="true" className={`absolute right-1/2 top-3.5 h-px w-full ${complete || current ? "bg-primary" : "bg-border"}`} />}
          <span aria-hidden="true" className={`relative mx-auto grid h-7 w-7 place-items-center rounded-full border text-xs font-semibold ${complete ? "border-primary bg-primary text-primary-foreground" : current ? "border-primary bg-background text-primary ring-2 ring-primary/20" : "border-border bg-background text-muted-foreground"}`}>
            {complete ? <Check className="h-4 w-4" /> : index + 1}
          </span>
          <span className={`mt-1 block px-0.5 text-xs ${current ? "font-semibold text-primary" : "text-muted-foreground"}`}><span className="sr-only">{complete ? tr("Completato: ") : current ? tr("Passaggio attuale: ") : tr("Da completare: ")}</span>{tr(step)}</span>
        </li>;
      })}
    </ol>
    {progress.confirmation && <p className="mt-2 text-center text-xs text-muted-foreground">{tr(progress.confirmation)}</p>}
  </div>;
}
